import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    AdminApiService,
    ApplianceItemArchiveEntity,
    ApplianceTypeEntity,
    GeneralApiService
} from 'app/api';
import { catchError, map, of, switchMap } from 'rxjs';
import * as SolarCollectorActions from './solar-collector.actions';
import { getSolarCollectorApplianceTypes } from './solar-collector.actions';

@Injectable()
export class SolarCollectorEffects {
    constructor(
        private actions$: Actions,
        public adminApiService: AdminApiService,
        public generalApiService: GeneralApiService,
        private readonly router: Router
    ) {}

    AdminGetSolarCollectorApplianceTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.getSolarCollectorApplianceTypes),
            switchMap(() => {
                return this.generalApiService
                    .getSolarCollectorApplianceTypes(/* { body: { queryDate: date } } */)
                    .pipe(
                        map((typeData: ApplianceTypeEntity[]) => {
                            return SolarCollectorActions.getSolarCollectorApplianceTypesSuccess({
                                solarCollectorApplianceTypes: typeData
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(
                                SolarCollectorActions.getSolarCollectorApplianceTypesFailure({
                                    error: error.error.message
                                })
                            );
                        })
                    );
            })
        )
    );

    AdminGetSolarCollectorApplianceTypeArchive$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.getSolarCollectorApplianceTypeArchive),
            switchMap(({ id }) => {
                return this.adminApiService.getRenewableApplianceItemHistory({ id }).pipe(
                    map((solarCollectorApplianceTypeArchives: ApplianceItemArchiveEntity[]) => {
                        return SolarCollectorActions.getSolarCollectorApplianceTypeArchiveSuccess({
                            id,
                            solarCollectorApplianceTypeArchives
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            SolarCollectorActions.getSolarCollectorApplianceTypeArchiveFailure({
                                error: error.error.message
                            })
                        );
                    })
                );
            })
        )
    );

    AdminEditSolarCollectorApplianceItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.editSolarCollectorApplianceItem),
            switchMap(({ id, ...body }) => {
                return this.adminApiService
                    .updateRenewableApplianceItem({
                        id,
                        body
                    })
                    .pipe(
                        map((newValueData) => {
                            return SolarCollectorActions.editSolarCollectorApplianceItemSuccess({
                                id,
                                name: newValueData.name,
                                value: newValueData.value
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(
                                SolarCollectorActions.editSolarCollectorApplianceItemFailure({
                                    error: error.error.message
                                })
                            );
                        })
                    );
            })
        )
    );

    editditSolarCollectorApplianceItemSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.editSolarCollectorApplianceItemSuccess),
            switchMap((action) => of(getSolarCollectorApplianceTypes({ date: null })))
        )
    );

    SaveSolarCollectorApplianceItemAdmin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.saveSolarCollectorApplianceItem),
            switchMap(({ id, ...body }) => {
                return this.adminApiService
                    .createSolarCollectorApplianceItem({
                        body: {
                            typeId: id,
                            ...body
                        }
                    })
                    .pipe(
                        map((applianceTypeData: ApplianceTypeEntity[]) =>
                            SolarCollectorActions.getSolarCollectorApplianceTypesSuccess({
                                solarCollectorApplianceTypes: applianceTypeData
                            })
                        ),
                        catchError((error: HttpErrorResponse) =>
                            of(
                                SolarCollectorActions.saveSolarCollectorApplianceItemFailure({
                                    error: error.error.message
                                })
                            )
                        )
                    );
            })
        )
    );

    DeleteSolarCollectorApplianceItemAdmin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.deleteSolarCollectorApplianceItem),
            switchMap(({ id }) =>
                this.adminApiService.deleteRenewableApplianceItem({ id }).pipe(
                    map((newValueData) =>
                        SolarCollectorActions.deleteSolarCollectorapplianceItemSuccess({
                            id
                        })
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(
                            SolarCollectorActions.deleteSolarCollectorApplianceItemFailure({
                                error: error.error.message
                            })
                        )
                    )
                )
            )
        )
    );

    deleteSolarCollectorApplianceItemSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.deleteSolarCollectorapplianceItemSuccess),
            switchMap((action) => of(getSolarCollectorApplianceTypes({ date: null })))
        )
    );

    public logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SolarCollectorActions.logout),
            map(() => SolarCollectorActions.clearSolarCollectorTypesData())
        )
    );
}
